import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Gift, Car, Wrench, Package, HelpCircle } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface EmployeePerk {
  id: string;
  employee_id: string;
  perk_date: string;
  perk_type: string;
  description: string;
  notes: string | null;
  created_by: string;
  created_at: string;
}

interface EmployeePerksTabProps {
  employeeId: string;
  readOnly?: boolean;
}

const perkTypes = [
  { value: "vehicle_loan", label: "Fahrzeug-Ausleihe", icon: Car },
  { value: "machine_loan", label: "Maschinen-Ausleihe", icon: Wrench },
  { value: "material", label: "Material", icon: Package },
  { value: "other", label: "Sonstiges", icon: HelpCircle },
];

const getPerkIcon = (type: string) => {
  const perkType = perkTypes.find(p => p.value === type);
  const Icon = perkType?.icon || Gift;
  return <Icon className="h-4 w-4" />;
};

const getPerkLabel = (type: string) => {
  return perkTypes.find(p => p.value === type)?.label || type;
};

export const EmployeePerksTab = ({ employeeId, readOnly = false }: EmployeePerksTabProps) => {
  const { user } = useAuth();
  const [perks, setPerks] = useState<EmployeePerk[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    perk_date: new Date().toISOString().split("T")[0],
    perk_type: "other",
    description: "",
    notes: "",
  });

  const fetchPerks = async () => {
    try {
      const { data, error } = await supabase
        .from("employee_perks")
        .select("*")
        .eq("employee_id", employeeId)
        .order("perk_date", { ascending: false });

      if (error) throw error;
      setPerks(data || []);
    } catch (error) {
      console.error("Error fetching perks:", error);
      toast.error("Fehler beim Laden der Annehmlichkeiten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerks();
  }, [employeeId]);

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error("Bitte eine Beschreibung eingeben");
      return;
    }

    try {
      const { error } = await supabase.from("employee_perks").insert({
        employee_id: employeeId,
        perk_date: formData.perk_date,
        perk_type: formData.perk_type,
        description: formData.description.trim(),
        notes: formData.notes.trim() || null,
        created_by: user?.id,
      });

      if (error) throw error;

      toast.success("Annehmlichkeit erfolgreich hinzugefügt");
      setDialogOpen(false);
      setFormData({
        perk_date: new Date().toISOString().split("T")[0],
        perk_type: "other",
        description: "",
        notes: "",
      });
      fetchPerks();
    } catch (error) {
      console.error("Error adding perk:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("employee_perks").delete().eq("id", id);
      if (error) throw error;
      toast.success("Gelöscht");
      fetchPerks();
    } catch (error) {
      console.error("Error deleting perk:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5" />
          Annehmlichkeiten / Goodies
        </CardTitle>
        {!readOnly && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Neue Annehmlichkeit erfassen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={formData.perk_date}
                    onChange={(e) => setFormData({ ...formData, perk_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Art</Label>
                  <Select
                    value={formData.perk_type}
                    onValueChange={(value) => setFormData({ ...formData, perk_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {perkTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Beschreibung *</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="z.B. Bus für Umzug ausgeliehen"
                  />
                </div>
                <div>
                  <Label>Notizen (optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Zusätzliche Informationen..."
                  />
                </div>
                <Button onClick={handleSubmit} className="w-full">
                  Speichern
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {perks.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            Keine Annehmlichkeiten erfasst
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Art</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead>Notizen</TableHead>
                {!readOnly && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {perks.map((perk) => (
                <TableRow key={perk.id}>
                  <TableCell>
                    {format(new Date(perk.perk_date), "dd.MM.yyyy", { locale: de })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getPerkIcon(perk.perk_type)}
                      {getPerkLabel(perk.perk_type)}
                    </div>
                  </TableCell>
                  <TableCell>{perk.description}</TableCell>
                  <TableCell className="text-muted-foreground">{perk.notes || "-"}</TableCell>
                  {!readOnly && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(perk.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
